import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation, useNavigate } from "react-router-dom";
import { FaFacebook, FaTiktok, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { Images } from "../../../helpers/Images";
import "./NavBar.css";

const competitionLinks = [
  { label: "Categories", path: "/categories" },
  { label: "Services", path: "/services" },
  { label: "History", path: "/history" },
  { label: "Gallery", path: "/gallery" },
  { label: "Zeko Run", path: "/game" },
];

const mainLinks = [
  { label: "Contact", path: "/contact" },
  { label: "About WDC", path: "/about" },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/WedoChallengeEgy?mibextid=ZbWKwL",
    Icon: FaFacebook,
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/+201044664265",
    Icon: FaWhatsapp,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@wedo_challenge?_t=8sgG40At7nc&_r=1",
    Icon: FaTiktok,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@WeDoChallenge",
    Icon: FaYoutube,
  },
];

const isActivePath = (pathname, path) => {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
};

const isCompetitionActive = (pathname) =>
  competitionLinks.some((link) => isActivePath(pathname, link.path)) ||
  pathname.startsWith("/competition");

const NavBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compMenuAnchor, setCompMenuAnchor] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const navigateTo = (path) => {
    navigate(path);
    setDrawerOpen(false);
    setCompMenuAnchor(null);
  };

  const linkSx = (active) => ({
    fontSize: "15px",
    fontWeight: 600,
    color: active ? "#23aef4" : "#595c61",
    cursor: "pointer",
    px: 1.5,
    py: 0.85,
    borderRadius: "10px",
    whiteSpace: "nowrap",
    transition: "color 0.2s ease, background-color 0.2s ease",
    backgroundColor: active ? "rgba(35, 174, 244, 0.10)" : "transparent",
    display: "flex",
    alignItems: "center",
    gap: 0.25,
    "&:hover": {
      color: "#23aef4",
      backgroundColor: "rgba(35, 174, 244, 0.08)",
    },
  });

  return (
    <Box
      component="header"
      sx={{
        width: "100%",
        height: { xs: "72px", md: "76px" },
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(89, 92, 97, 0.10)",
        boxShadow: "0 4px 24px rgba(10, 14, 24, 0.06)",
      }}
    >
      <Box
        sx={{
          height: "100%",
          maxWidth: "1500px",
          margin: "0 auto",
          px: { xs: "20px", sm: "40px", lg: "80px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ width: "18%" }}>
          <Box
            component="img"
            src={Images.codeLogoHor}
            alt="We Do Challenge Championship"
            onClick={() => navigateTo("/")}
            sx={{
              height: { xs: "42px", md: "48px" },
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 0.5,
            flex: 1,
            justifyContent: "center",
          }}
        >
          <Typography
            onClick={() => navigateTo("/")}
            sx={linkSx(isActivePath(pathname, "/"))}
          >
            Home
          </Typography>

          <Typography
            onClick={(e) => setCompMenuAnchor(e.currentTarget)}
            sx={linkSx(isCompetitionActive(pathname))}
          >
            Competition
            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
          </Typography>

          <Menu
            anchorEl={compMenuAnchor}
            open={Boolean(compMenuAnchor)}
            onClose={() => setCompMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "14px",
                minWidth: 200,
                border: "1px solid rgba(89, 92, 97, 0.10)",
                boxShadow: "0 16px 40px rgba(10, 14, 24, 0.12)",
              },
            }}
          >
            {competitionLinks.map((link) => (
              <MenuItem
                key={link.path}
                onClick={() => navigateTo(link.path)}
                selected={isActivePath(pathname, link.path)}
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: isActivePath(pathname, link.path)
                    ? "#23aef4"
                    : "#595c61",
                  py: 1.25,
                  "&.Mui-selected": {
                    backgroundColor: "rgba(35, 174, 244, 0.10)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(35, 174, 244, 0.14)",
                  },
                }}
              >
                {link.label}
              </MenuItem>
            ))}
          </Menu>

          {mainLinks.map((link) => (
            <Typography
              key={link.path}
              onClick={() => navigateTo(link.path)}
              sx={linkSx(isActivePath(pathname, link.path))}
            >
              {link.label}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {socialLinks.map(({ label, href, Icon }) => (
              <Box
                key={label}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="nav-social-link"
              >
                <Icon />
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            onClick={() => navigateTo("/firststage")}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "14px",
              px: 2.5,
              py: 0.9,
              whiteSpace: "nowrap",
              color: "#fff",
              background: "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
              boxShadow: "0 8px 20px rgba(193, 26, 131, 0.22)",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #1e9de0 0%, #a8156e 100%)",
                boxShadow: "0 10px 24px rgba(193, 26, 131, 0.28)",
              },
            }}
          >
            Register
          </Button>
        </Box>

        <IconButton
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
          sx={{
            display: { xs: "flex", md: "none" },
            color: "#595c61",
            border: "1px solid rgba(89, 92, 97, 0.14)",
            borderRadius: "12px",
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className="nav-drawer"
      >
        <Box
          sx={{
            width: { xs: "100vw", sm: 340 },
            maxWidth: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            background:
              "linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 2,
              borderBottom: "1px solid rgba(89, 92, 97, 0.10)",
            }}
          >
            <Box
              component="img"
              src={Images.codeLogoHor}
              alt="We Do Challenge Championship"
              onClick={() => navigateTo("/")}
              sx={{ height: "44px", cursor: "pointer" }}
            />
            <IconButton
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              sx={{ color: "#595c61" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
            <Typography
              className="nav-drawer-link"
              data-active={isActivePath(pathname, "/")}
              onClick={() => navigateTo("/")}
            >
              Home
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#23aef4",
                px: 1.5,
                pt: 2,
                pb: 1,
              }}
            >
              Competition
            </Typography>
            {competitionLinks.map((link) => (
              <Typography
                key={link.path}
                className="nav-drawer-link nav-drawer-link--sub"
                data-active={isActivePath(pathname, link.path)}
                onClick={() => navigateTo(link.path)}
              >
                {link.label}
              </Typography>
            ))}

            <Divider sx={{ my: 2, borderColor: "rgba(89, 92, 97, 0.10)" }} />

            <Typography
              className="nav-drawer-link"
              data-active={isActivePath(pathname, "/firststage")}
              onClick={() => navigateTo("/firststage")}
            >
              Registration
            </Typography>

            {mainLinks.map((link) => (
              <Typography
                key={link.path}
                className="nav-drawer-link"
                data-active={isActivePath(pathname, link.path)}
                onClick={() => navigateTo(link.path)}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2.5,
              borderTop: "1px solid rgba(89, 92, 97, 0.10)",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigateTo("/firststage")}
              sx={{
                mb: 2,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "15px",
                py: 1.2,
                color: "#fff",
                background:
                  "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                boxShadow: "0 8px 20px rgba(193, 26, 131, 0.22)",
              }}
            >
              Register Now
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {socialLinks.map(({ label, href, Icon }) => (
                <Box
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="nav-social-link nav-social-link--drawer"
                >
                  <Icon />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default NavBar;
